/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './css/AdminOrderModal.css';
import '../AdminCreateOrderModal/css/AdminCreateOrderModal.css';
import {
  archiveOrder,
  declineOrder,
  deleteOrder,
  replaceOrderTour,
  updateOrderPeople,
  updateOrderContacts,
  updateOrderPrice,
  updateOrderFlight,
} from '../../../Utils/RestUtils';
import {
  firstLetterInUpperCase,
  formatPrice,
  getOrderStage,
} from '../../../Utils/CommonUtils';
import { isSameTourists, getSplitPrice } from '../../../Utils/OrderUtil';
import { emailValidation } from '../../../Utils/ValidationUtil';
import {
  SUCCESS_STATUS,
  SUCCESS_TEXT,
  ERROR_TEXT,
  BYN_TEXT,
  PROFILE_ORDER_FLIGHT_TO,
  PROFILE_ORDER_FLIGHT_BACK,
  PROFILE_ORDER_TOTAL,
  ORDER_DECLINED_STATUS,
  INCORRECT_EMAIL_FORMAT,
  CHOOSE_ANOTHER,
} from '../../../Utils/Constants/GlobalConstants';
import {
  ORDER_FIELD_SECTIONS,
  CONTACT_INFO_TITLE,
  TOURIST_TITLE,
  DECLINE_ORDER,
  DECLINE_ORDER_CONFIRM,
  DECLINE_ORDER_HINT,
  REPLACE_TOUR,
  REPLACE_TOUR_CONFIRM,
  REPLACE_TOUR_CONFIRM_HINT,
  REPLACE_TOUR_TITLE,
  REPLACE_TOUR_HINT,
  REPLACE_TOUR_TOURISTS_MISMATCH,
  REPLACE_TOUR_WITHOUT_FLIGHT,
  STAGE_OPERATOR_REQUEST,
  REPLACE_TOUR_STAGES,
  DELETE,
  DELETE_CONFIRM,
  EMPTY_VALUE,
  YES,
  NO,
  ORDER_NUMBER_TITLE,
  PEOPLE_TITLE,
  TO_ARCHIVE,
  TO_ARCHIVE_CONFIRM,
  TO_ARCHIVE_HINT,
  ARCHIVED_PEOPLE_REMOVED,
  ARCHIVED_BADGE,
  PERSON_EMPTY,
  EDIT,
  SAVE,
  CANCEL,
  CONTACT_EDIT_FIELDS,
  TOURIST_EDIT_FIELDS,
  ORDER_CONTACTS_EDIT_FIELDS,
  ORDER_PRICE_EDIT_FIELDS,
  ORDER_PRICE_INVALID,
  BYN_CURRENCY,
} from '../../../Utils/Constants/AdminConstants';
import OrderStatusBadge from '../OrderStatusBadge/OrderStatusBadge';
import OrderStageBadge from '../OrderStageBadge/OrderStageBadge';
import AdminPersonForm from '../AdminPersonForm/AdminPersonForm';
import AdminTourActualization from '../AdminTourActualization/AdminTourActualization';
import AdminFlightChoice from '../AdminFlightChoice/AdminFlightChoice';
import OrderFacts from '../../OrderFacts/OrderFacts';
import OrderTicket from '../../OrderTicket/OrderTicket';
import OrderPerson from '../../OrderPerson/OrderPerson';
import PriceText from '../../PriceText/PriceText';
import { ReactComponent as Close } from '../../../images/close-r.svg';

const CONTACT_KEY = 'contact';
// the order email, phone and note, edited the same way as a person
const ORDER_CONTACTS_KEY = 'orderContacts';
const ORDER_PRICE_KEY = 'orderPrice';
const ORDER_FLIGHT_KEY = 'orderFlight';

const isPersonEmpty = (person) =>
  !person || Object.values(person).every((value) => !value);

const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

function AdminOrderModal(props) {
  const {
    isVisible,
    modalNode,
    handleModalClick,
    order,
    handleOrderUpdate,
    handleOrderDelete,
  } = props;
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
  const [isArchiveConfirm, setIsArchiveConfirm] = useState(false);
  const [isDeclineConfirm, setIsDeclineConfirm] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  // the order built from the actualized new tour, null until actualized
  const [replaceTourOrder, setReplaceTourOrder] = useState(null);
  const [isReplaceConfirm, setIsReplaceConfirm] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [message, setMessage] = useState('');
  // CONTACT_KEY or a tourist index, one person is edited at a time
  const [editingKey, setEditingKey] = useState(null);
  const [personDraft, setPersonDraft] = useState({});
  // the draft as the form was opened, to save only a changed one
  const [initialDraft, setInitialDraft] = useState({});
  const orderId = order ? order.id : null;

  // ask for delete confirmation again for every opened order
  useEffect(() => {
    setIsDeleteConfirm(false);
    setIsArchiveConfirm(false);
    setIsDeclineConfirm(false);
    setIsReplaceMode(false);
    setReplaceTourOrder(null);
    setIsReplaceConfirm(false);
    setEditingKey(null);
    setMessage('');
  }, [isVisible, orderId]);

  // the success message hides itself, an error stays until the next action
  useEffect(() => {
    if (message !== SUCCESS_TEXT) {
      return undefined;
    }

    const timeoutId = setTimeout(() => setMessage(''), 3000);

    return () => clearTimeout(timeoutId);
  }, [message]);

  // the modal makes the requests itself, so it works from any list of orders
  const sendRequest = (request, handleSuccess) => {
    const token = window.localStorage.getItem('accessToken');

    setIsPending(true);
    setMessage('');

    return request(token)
      .then((response) => {
        const isSuccess = handleSuccess(response);
        setMessage(isSuccess ? SUCCESS_TEXT : ERROR_TEXT);

        return isSuccess;
      })
      .catch(() => {
        setMessage(ERROR_TEXT);

        return false;
      })
      .finally(() => {
        setIsPending(false);
      });
  };

  // statuses "waits for approval" and "approved" follow the stages,
  // declining is possible only while the operator request is open
  const canDecline = () =>
    !!order &&
    !order.archived &&
    getOrderStage(order) === STAGE_OPERATOR_REQUEST;

  // the tour is replaced only before the operator approves the booking
  const canReplace = () =>
    !!order &&
    !order.archived &&
    REPLACE_TOUR_STAGES.includes(getOrderStage(order));

  const isReplaceTouristsMismatch = () =>
    !!replaceTourOrder && !isSameTourists(order, replaceTourOrder);

  const toggleReplaceMode = (isEnabled) => {
    setIsReplaceMode(isEnabled);
    setReplaceTourOrder(null);
    setIsReplaceConfirm(false);
    setIsDeclineConfirm(false);
    setIsArchiveConfirm(false);
    setEditingKey(null);
    setMessage('');
  };

  // another tour or price needs a new confirmation
  const changeReplaceTourOrder = (tourOrder) => {
    setReplaceTourOrder(tourOrder);
    setIsReplaceConfirm(false);
  };

  // the backend restarts the order and emails the customer the new tour
  const replaceTour = () => {
    if (!replaceTourOrder || isReplaceTouristsMismatch()) {
      return;
    }

    if (!isReplaceConfirm) {
      setIsReplaceConfirm(true);
      return;
    }

    setIsReplaceConfirm(false);

    sendRequest(
      (token) => replaceOrderTour(order.id, replaceTourOrder, token),
      (response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(order.id, response.data);
          return true;
        }

        return false;
      },
    ).then((isReplaced) => {
      if (isReplaced) {
        setIsReplaceMode(false);
        setReplaceTourOrder(null);
      }
    });
  };

  // the backend also emails the customer and archives the order
  const decline = () => {
    if (!isDeclineConfirm) {
      setIsDeclineConfirm(true);
      setIsArchiveConfirm(false);
      return;
    }

    setIsDeclineConfirm(false);
    setEditingKey(null);

    sendRequest(
      (token) => declineOrder(order.id, token),
      (response) => {
        if (response && response.data === SUCCESS_STATUS) {
          handleOrderUpdate(order.id, {
            status: ORDER_DECLINED_STATUS,
            archived: true,
            contactInfo: null,
            tourists: [],
          });
          return true;
        }

        return false;
      },
    );
  };

  // one way: an archived order is not returned to the board,
  // the backend removes the buyer and tourists data
  const moveToArchive = () => {
    if (!isArchiveConfirm) {
      setIsArchiveConfirm(true);
      setIsDeclineConfirm(false);
      return;
    }

    setIsArchiveConfirm(false);
    setEditingKey(null);

    sendRequest(
      (token) => archiveOrder(order.id, token),
      (response) => {
        if (response && response.data === SUCCESS_STATUS) {
          handleOrderUpdate(order.id, {
            archived: true,
            contactInfo: null,
            tourists: [],
          });
          return true;
        }

        return false;
      },
    );
  };

  const removeOrder = () => {
    const { id } = order;

    sendRequest(
      (token) => deleteOrder(id, token),
      (response) => response && response.data === SUCCESS_STATUS,
    ).then((isDeleted) => {
      if (isDeleted) {
        handleOrderDelete(id);
      }
    });
  };

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleEscPress = (e) => {
      if (e.key === 'Escape') {
        handleModalClick();
      }
    };

    document.addEventListener('keydown', handleEscPress, false);

    return () => {
      document.removeEventListener('keydown', handleEscPress, false);
    };
  }, [isVisible, handleModalClick]);

  const getFieldValue = (key, value) => {
    if (typeof value === 'boolean') {
      return value ? YES : NO;
    }

    if (Array.isArray(value)) {
      return value.map((item, index) => (
        <div key={`aom-value-${index}`}>{item}</div>
      ));
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  };

  const renderRow = (key, label, value) => (
    <div key={`aom-row-${key}`} className="aom_row">
      <div className="aom_label gr_text_14">{label}</div>
      <div className="aom_value br_text_16">
        {isEmptyValue(value) ? EMPTY_VALUE : getFieldValue(key, value)}
      </div>
    </div>
  );

  const renderSection = (title, rows, key) => (
    <div key={`aom-section-${key}`} className="aom_section">
      <div className="bm_text_18 mb_12">{title}</div>
      <div className="aom_rows">{rows}</div>
    </div>
  );

  const hasField = (section, key) =>
    section.fields.some((field) => field.key === key);

  const renderTickets = () => {
    const hasTo = !isEmptyValue(order.toTrip);
    const hasBack = !isEmptyValue(order.backTrip);

    if (!hasTo && !hasBack) {
      return <div className="br_text_16">{EMPTY_VALUE}</div>;
    }

    return (
      <div className="aom_tickets">
        {hasTo && (
          <OrderTicket title={PROFILE_ORDER_FLIGHT_TO} segments={order.toTrip} />
        )}
        {hasBack && (
          <OrderTicket
            title={PROFILE_ORDER_FLIGHT_BACK}
            segments={order.backTrip}
          />
        )}
      </div>
    );
  };

  const renderTotal = (title, action) => (
    <div className="aom_total">
      <div className="aom_section_head">
        <div className="bm_text_18">{title}</div>
        {action}
      </div>
      {!isEmptyValue(order.totalPriceByn) && (
        <div className="aom_total_value">
          <div className="gr_text_13">{PROFILE_ORDER_TOTAL}</div>
          <div className="bb_text_22">
            <PriceText text={`${formatPrice(order.totalPriceByn)} ${BYN_TEXT}`} />
          </div>
        </div>
      )}
    </div>
  );

  // a manual order has no tourists yet: empty cards by the order people count
  const getTourists = () => {
    if (order.tourists && order.tourists.length > 0) {
      return order.tourists;
    }

    const count =
      (parseInt(order.adultsCount, 10) || 0) +
      (parseInt(order.kidsCount, 10) || 0);

    return Array.from({ length: count || 1 }, () => ({}));
  };

  const startEditing = (key, person) => {
    setPersonDraft({ ...(person || {}) });
    setInitialDraft({ ...(person || {}) });
    setEditingKey(key);
  };

  const changeDraft = (key, value) => {
    setPersonDraft((draft) => ({ ...draft, [key]: value }));
  };

  const savePerson = () => {
    const tourists = getTourists().map((tourist, index) =>
      (index === editingKey ? personDraft : tourist));

    const people = {
      contactInfo:
        editingKey === CONTACT_KEY ? personDraft : order.contactInfo || null,
      tourists,
    };

    sendRequest(
      (token) => updateOrderPeople(order.id, people, token),
      (response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(order.id, {
            contactInfo: response.data.contactInfo,
            tourists: response.data.tourists,
          });
          return true;
        }

        return false;
      },
    ).then((isSaved) => {
      if (isSaved) {
        setEditingKey(null);
      }
    });
  };

  const saveContacts = () => {
    const contacts = {
      email: (personDraft.email || '').trim(),
      phoneNumber: (personDraft.phoneNumber || '').trim(),
      note: (personDraft.note || '').trim(),
    };

    if (!emailValidation(contacts.email)) {
      setMessage(INCORRECT_EMAIL_FORMAT);
      return;
    }

    sendRequest(
      (token) => updateOrderContacts(order.id, contacts, token),
      (response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(order.id, {
            email: response.data.email,
            phoneNumber: response.data.phoneNumber,
            note: response.data.note,
          });
          return true;
        }

        return false;
      },
    ).then((isSaved) => {
      if (isSaved) {
        setEditingKey(null);
      }
    });
  };

  // the split payment and the alfa sums follow the total, as on checkout
  const getPriceFields = (draft) => {
    const total = parseFloat(draft.totalPriceByn);
    const split = parseInt(draft.split, 10);

    if (!total || !split || split < 1 || split > 100) {
      return null;
    }

    const splitPaymentValue = getSplitPrice(total, split);

    return {
      originalPrice: draft.originalPrice || '',
      currency: draft.currency,
      totalPriceByn: `${total}`,
      totalPriceAlfa: `${Math.round(total * 100)}`,
      flightInsurance: draft.flightInsurance,
      flightInsuranceValueByn: draft.flightInsuranceValueByn || '',
      split: `${split}`,
      splitPayment: `${splitPaymentValue} ${BYN_CURRENCY}`,
      splitPaymentValue: `${splitPaymentValue}`,
      splitPaymentAlfa: `${splitPaymentValue * 100}`,
    };
  };

  const savePrice = () => {
    const price = getPriceFields(personDraft);

    if (!price) {
      setMessage(ORDER_PRICE_INVALID);
      return;
    }

    sendRequest(
      (token) => updateOrderPrice(order.id, price, token),
      (response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(order.id, response.data);
          return true;
        }

        return false;
      },
    ).then((isSaved) => {
      if (isSaved) {
        setEditingKey(null);
      }
    });
  };

  const saveFlight = (flightOrder) =>
    sendRequest(
      (token) => updateOrderFlight(order.id, flightOrder, token),
      (response) => {
        if (response && response.data && response.data.id) {
          handleOrderUpdate(order.id, response.data);
          return true;
        }

        return false;
      },
    ).then((isSaved) => {
      if (isSaved) {
        setEditingKey(null);
      }

      return isSaved;
    });

  const renderEditButton = (key, person) => (
    <button
      type="button"
      className="ap_small_btn light_gray_button bm_text_14"
      disabled={isPending || editingKey !== null}
      onClick={() => startEditing(key, person)}
    >
      {EDIT}
    </button>
  );

  // an empty field and a missing value are the same
  const getDraftValue = (value) =>
    (value === undefined || value === null ? '' : `${value}`);

  const isDraftChanged = () =>
    Object.keys({ ...initialDraft, ...personDraft }).some(
      (key) =>
        getDraftValue(personDraft[key]) !== getDraftValue(initialDraft[key]),
    );

  const renderFormButtons = (handleSave) => (
    <div className="aom_people_buttons">
      <button
        type="button"
        className="ap_small_btn light_gray_button bm_text_14"
        disabled={isPending}
        onClick={() => setEditingKey(null)}
      >
        {CANCEL}
      </button>
      <button
        type="button"
        className="ap_small_btn btn__yellow bm_text_14"
        disabled={isPending || !isDraftChanged()}
        onClick={handleSave}
      >
        {SAVE}
      </button>
    </div>
  );

  const renderContactsSection = (section, rows) => {
    if (editingKey === ORDER_CONTACTS_KEY) {
      return (
        <AdminPersonForm
          key={`aom-section-${section.title}`}
          title={section.title}
          fields={ORDER_CONTACTS_EDIT_FIELDS}
          person={personDraft}
          handleChange={changeDraft}
          actions={renderFormButtons(saveContacts)}
        />
      );
    }

    return (
      <div key={`aom-section-${section.title}`} className="aom_section">
        <div className="aom_section_head mb_12">
          <div className="bm_text_18">{section.title}</div>
          {!order.archived &&
            renderEditButton(ORDER_CONTACTS_KEY, {
              email: order.email,
              phoneNumber: order.phoneNumber,
              note: order.note,
            })}
        </div>
        <div className="aom_rows">{rows}</div>
      </div>
    );
  };

  const renderPriceSection = (section, rows) => {
    if (editingKey === ORDER_PRICE_KEY) {
      return (
        <AdminPersonForm
          key={`aom-section-${section.title}`}
          title={section.title}
          fields={ORDER_PRICE_EDIT_FIELDS}
          person={personDraft}
          handleChange={changeDraft}
          actions={renderFormButtons(savePrice)}
        />
      );
    }

    const action =
      !order.archived &&
      renderEditButton(ORDER_PRICE_KEY, {
        ...ORDER_PRICE_EDIT_FIELDS.reduce(
          (result, field) => ({ ...result, [field.key]: order[field.key] }),
          {},
        ),
        flightInsurance: `${order.flightInsurance}` === 'true' ? 'true' : 'false',
        split: order.split || '100',
      });

    return (
      <div key={`aom-section-${section.title}`} className="aom_section">
        {renderTotal(section.title, action)}
        <div className="aom_rows">{rows}</div>
      </div>
    );
  };

  // another flight only of the same tour, so the order needs its tour id
  const renderFlightSection = (section) => (
    <div key={`aom-section-${section.title}`} className="aom_section">
      <div className="bm_text_18 mb_12">{section.title}</div>
      {renderTickets()}
      {!order.archived && order.tourId && editingKey !== ORDER_FLIGHT_KEY && (
        <button
          type="button"
          className="ap_small_btn light_gray_button bm_text_14 aom_choose_flight"
          disabled={isPending || editingKey !== null}
          onClick={() => setEditingKey(ORDER_FLIGHT_KEY)}
        >
          {CHOOSE_ANOTHER}
        </button>
      )}
      {editingKey === ORDER_FLIGHT_KEY && (
        <AdminFlightChoice
          order={order}
          isPending={isPending}
          handleSave={saveFlight}
          handleCancel={() => setEditingKey(null)}
        />
      )}
    </div>
  );

  const renderOrderSection = (section) => {
    // the flight is shown as tickets, like in the user cabinet
    if (hasField(section, 'toTrip')) {
      return renderFlightSection(section);
    }

    const rows = section.fields
      .filter((field) => !field.hideEmpty || !isEmptyValue(order[field.key]))
      .map((field) => {
        const value = order[field.key];
        return renderRow(field.key, field.label, value);
      });

    if (hasField(section, 'email')) {
      return renderContactsSection(section, rows);
    }

    if (hasField(section, 'totalPriceByn')) {
      return renderPriceSection(section, rows);
    }

    return renderSection(section.title, rows, section.title);
  };

  const renderPerson = (key, title, fields, person) => {
    if (editingKey === key) {
      return (
        <AdminPersonForm
          key={`aom-person-${key}`}
          title={title}
          fields={fields}
          person={personDraft}
          handleChange={changeDraft}
          actions={renderFormButtons(savePerson)}
        />
      );
    }

    if (isPersonEmpty(person)) {
      return (
        <div key={`aom-person-${key}`} className="aom_section aom_person_empty">
          <div>
            <div className="bm_text_18 mb_5">{title}</div>
            <div className="gr_text_14">{PERSON_EMPTY}</div>
          </div>
          {renderEditButton(key, person)}
        </div>
      );
    }

    return (
      <OrderPerson
        key={`aom-person-${key}`}
        title={title}
        person={person}
        action={renderEditButton(key, person)}
      />
    );
  };

  const renderPeopleCards = () => (
    <>
      {renderPerson(
        CONTACT_KEY,
        CONTACT_INFO_TITLE,
        CONTACT_EDIT_FIELDS,
        order.contactInfo,
      )}
      {getTourists().map((tourist, index) =>
        renderPerson(
          index,
          `${TOURIST_TITLE} ${index + 1}`,
          TOURIST_EDIT_FIELDS,
          tourist,
        ))}
    </>
  );

  const renderPeople = () => (
    <>
      <div className="bm_text_20 aom_people_title">{PEOPLE_TITLE}</div>
      {order.archived ? (
        <div className="aom_section gr_text_14">{ARCHIVED_PEOPLE_REMOVED}</div>
      ) : (
        renderPeopleCards()
      )}
    </>
  );

  const renderReplaceBody = () => (
    <div className="aom_body acom_form custom_scroll_container">
      <div>
        <div className="bm_text_20 mb_5">{REPLACE_TOUR_TITLE}</div>
        <div className="gr_text_14">{REPLACE_TOUR_HINT}</div>
      </div>
      <AdminTourActualization
        isVisible={isVisible}
        handleOrderChange={changeReplaceTourOrder}
        withoutFlightText={REPLACE_TOUR_WITHOUT_FLIGHT}
      />
      {isReplaceTouristsMismatch() && (
        <div className="ap_message ap_message_error br_text_14">
          {REPLACE_TOUR_TOURISTS_MISMATCH}
        </div>
      )}
    </div>
  );

  const renderBody = () => (
    <div className="aom_body custom_scroll_container">
      <OrderFacts order={order} />
      {ORDER_FIELD_SECTIONS.map(renderOrderSection)}
      {renderPeople()}
    </div>
  );

  const handleDeleteClick = () => {
    if (isDeleteConfirm) {
      removeOrder();
    } else {
      setIsDeleteConfirm(true);
    }
  };

  const renderDeclineButton = () => (
    <button
      type="button"
      className={`aom_btn wm_text_16 ${
        isDeclineConfirm ? 'aom_btn_delete_confirm' : 'aom_btn_decline'
      }`}
      disabled={isPending}
      onClick={decline}
    >
      {isDeclineConfirm ? DECLINE_ORDER_CONFIRM : DECLINE_ORDER}
    </button>
  );

  const getMessageClass = () =>
    (message === SUCCESS_TEXT ? 'aom_message_success' : 'aom_message_error');

  // shown in the actions line, next to the buttons
  const renderMessage = () =>
    message.length > 0 && (
      <div className={`aom_message bm_text_14 ${getMessageClass()}`}>
        {message === SUCCESS_TEXT && <span>&#10003;</span>}
        {message}
      </div>
    );

  const renderReplaceActions = () => (
    <div className="aom_actions_buttons">
      <button
        type="button"
        className="aom_btn light_gray_button bm_text_16"
        disabled={isPending}
        onClick={() => toggleReplaceMode(false)}
      >
        {CANCEL}
      </button>
      <button
        type="button"
        className="aom_btn btn__yellow bm_text_16"
        disabled={isPending || !replaceTourOrder || isReplaceTouristsMismatch()}
        onClick={replaceTour}
      >
        {isReplaceConfirm ? REPLACE_TOUR_CONFIRM : REPLACE_TOUR}
      </button>
      {isReplaceConfirm && (
        <div className="rr_text_14">{REPLACE_TOUR_CONFIRM_HINT}</div>
      )}
      {renderMessage()}
    </div>
  );

  const renderActionsLine = () => (
    <div className="aom_actions_line">
      <div className="aom_actions_buttons">
        {canDecline() && renderDeclineButton()}
        {canReplace() && (
          <button
            type="button"
            className="aom_btn light_gray_button bm_text_16"
            disabled={isPending}
            onClick={() => toggleReplaceMode(true)}
          >
            {REPLACE_TOUR}
          </button>
        )}
        {isDeclineConfirm && (
          <div className="rr_text_14">{DECLINE_ORDER_HINT}</div>
        )}
        {renderMessage()}
      </div>
      <div className="aom_actions_buttons">
        {isArchiveConfirm && (
          <div className="rr_text_14">{TO_ARCHIVE_HINT}</div>
        )}
        {!order.archived && (
          <button
            type="button"
            className={`aom_btn bm_text_16 ${
              isArchiveConfirm ? 'aom_btn_delete_confirm' : 'light_gray_button'
            }`}
            disabled={isPending}
            onClick={moveToArchive}
          >
            {isArchiveConfirm ? TO_ARCHIVE_CONFIRM : TO_ARCHIVE}
          </button>
        )}
        {/* only an archived order can be deleted */}
        {order.archived && (
          <button
            type="button"
            className={`aom_btn bm_text_16 ${
              isDeleteConfirm ? 'aom_btn_delete_confirm' : 'light_gray_button'
            }`}
            disabled={isPending}
            onClick={handleDeleteClick}
          >
            {isDeleteConfirm ? DELETE_CONFIRM : DELETE}
          </button>
        )}
      </div>
    </div>
  );

  const renderActions = () => (
    <div className="aom_actions">
      {isReplaceMode ? (
        renderReplaceActions()
      ) : (
        renderActionsLine()
      )}
    </div>
  );

  // portal: the modal also opens on top of the user modal
  return createPortal(
    <div
      className={`aom_container aom_top ${isVisible ? 'aom_open' : ''}`}
    >
      <div
        className="aom_modal"
        ref={(node) => {
          modalNode.current = node;
        }}
      >
        {order && (
          <div className="aom_title_line">
            <div>
              <div className="aom_title mb_5">
                {`${ORDER_NUMBER_TITLE}${order.externalId}`}
              </div>
              {order.hotelName && (
                <div className="br_text_16 mb_5">
                  {firstLetterInUpperCase(order.hotelName)}
                  {order.hotelCategory && (
                    <span className="aom_category">
                      {` ${order.hotelCategory}*`}
                    </span>
                  )}
                </div>
              )}
              {order.countryTo && (
                <div className="gr_text_14 mb_12">
                  {[order.resort, order.countryTo]
                    .filter((item) => item)
                    .join(', ')}
                </div>
              )}
              <div className="aom_badges">
                {order.archived ? (
                  <span className="aom_archived bm_text_14">
                    {ARCHIVED_BADGE}
                  </span>
                ) : (
                  <>
                    <OrderStatusBadge status={order.status} />
                    <OrderStageBadge order={order} />
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              className="aom_close_but"
              onClick={handleModalClick}
            >
              <Close />
            </button>
          </div>
        )}

        {/* remount on open to reset scroll */}
        {isVisible && order && (isReplaceMode ? renderReplaceBody() : renderBody())}

        {order && renderActions()}
      </div>
    </div>,
    document.body,
  );
}

AdminOrderModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  modalNode: PropTypes.instanceOf(Object).isRequired,
  handleModalClick: PropTypes.func.isRequired,
  order: PropTypes.instanceOf(Object),
  handleOrderUpdate: PropTypes.func.isRequired,
  handleOrderDelete: PropTypes.func.isRequired,
};

AdminOrderModal.defaultProps = {
  order: null,
};

export default AdminOrderModal;
